import { Component, inject, OnInit, output, signal, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { Sort } from '@angular/material/sort';
import { OrderType, SearchViewModel } from '@models/module/grid';
import {
  CategoryGroupService,
  SOVCategoryGroupRow,
  SOVCategoryGroupTableOptions,
} from '@module-definition/category-group/common';
import {
  ActionEventType,
  actionTypeFromDropdownItem,
  DisplayFormats,
  FiltersToApply,
  TableColumn,
  TablePaginator,
} from '@shared/components/data-list/common';
import { DataListCommandComponent } from '@shared/components/data-list/data-list-command/data-list-command.component';
import { PagedDataListComponent } from '@shared/components/data-list/paged-data-list/paged-data-list.component';
import { DROPDOWN_ITEMS, DropdownActionsEnum, PAGE_SIZE_OPTIONS } from '@shared/consts';
import { CipoDialogModule } from '@shared/modules';
import { filter, finalize } from 'rxjs';
import { CategoryGroupViewComponent } from './category-group-view/category-group-view.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialog } from '@shared/dialogs';
import { NotificationService } from '@shared/services';

@Component({
  selector: 'cipo-category-group',
  imports: [
    MatIconModule,
    MatButtonModule,
    CipoDialogModule,
    DataListCommandComponent,
    PagedDataListComponent,
    CategoryGroupViewComponent,
  ],
  templateUrl: './category-group.component.html',
})
export class CategoryGroupComponent implements OnInit {
  dialog = inject(MatDialog);
  notification = inject(NotificationService);
  categoryGroupService = inject(CategoryGroupService);

  categoryGroupViewComponent = viewChild(CategoryGroupViewComponent);
  currentCategoryGroup = signal<SOVCategoryGroupRow>(null);
  onClose = output<boolean>({ alias: 'onclose' });
  anythingChanged = signal(false);

  tableColumns: TableColumn[] = [
    {
      id: 1,
      name: 'name',
      displayName: 'categoryGroup.nameColumn',
      width: 3,
      sortable: true,
    },
    {
      id: 2,
      name: 'isUsed',
      displayName: 'categoryGroup.isUsedColumn',
      displayFormatId: DisplayFormats.YesNo,
      sortable: true,
    },
    {
      id: 3,
      name: 'createdOn',
      displayName: 'categoryGroup.createdOnColumn',
      displayFormatId: DisplayFormats.Date,
      sortable: true,
    },
    {
      id: 4,
      name: 'createdBy',
      displayName: 'categoryGroup.createdByColumn',
      displayFormatId: DisplayFormats.Text,
      sortable: true,
    },
    {
      id: 5,
      name: 'disabled',
      displayName: 'categoryGroup.disabledColumn',
      displayFormatId: DisplayFormats.YesNo,
      sortable: true,
    },
  ];
  tableOptions = signal<SOVCategoryGroupTableOptions>({
    clickableRows: true,
    selectable: false,
    serverPaginationSort: true,
    pagination: {
      pageIndex: 0,
      pageSize: PAGE_SIZE_OPTIONS.long[1],
      pageSizeOptions: PAGE_SIZE_OPTIONS.default,
    },
    actions: () => {
      return [
        {
          ...actionTypeFromDropdownItem(DROPDOWN_ITEMS.EDIT),
          handleActionExternally: true,
        },
        {
          ...actionTypeFromDropdownItem(DROPDOWN_ITEMS.ENABLE),
          handleActionExternally: true,
          condition: (row: SOVCategoryGroupRow) => row.disabled,
        },
        {
          ...actionTypeFromDropdownItem(DROPDOWN_ITEMS.DISABLE),
          handleActionExternally: true,
          condition: (row: SOVCategoryGroupRow) => !row.disabled,
        },
        {
          ...actionTypeFromDropdownItem(DROPDOWN_ITEMS.DUPLICATE),
          handleActionExternally: true,
        },
        {
          ...actionTypeFromDropdownItem(DROPDOWN_ITEMS.DELETE),
          handleActionExternally: true,
          condition: (row: SOVCategoryGroupRow) => !row.isUsed,
        },
      ];
    },
  });
  tableData = signal<SOVCategoryGroupRow[]>(undefined);
  isLoading = signal(false);

  searchOptions: SearchViewModel = {
    page: 1,
    pagesize: this.tableOptions().pagination.pageSize,
  };

  ngOnInit(): void {
    this.loadCategoryGroups();
  }

  loadCategoryGroups() {
    this.isLoading.set(true);
    this.tableData.set(undefined);
    this.categoryGroupService
      .search(this.searchOptions)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(response => {
        this.tableData.set(
          response.data.map(item => ({
            ...item,
            categories: (item.categories ?? []).map(cat => ({ ...cat, uniqueKey: crypto.randomUUID(), visible: true })),
          })),
        );
        this.tableOptions.update(opt => ({ ...opt, pagination: { ...opt.pagination, length: response.records } }));
      });
  }

  search({ criteria }: FiltersToApply) {
    this.searchOptions.criteria = criteria;
    this.searchOptions.page = 1;
    this.tableData.set(null);
    this.tableOptions.update(options => {
      options.pagination.pageIndex = 0;
      return options;
    });

    this.loadCategoryGroups();
  }

  handleSort(event: Sort) {
    this.searchOptions.sort = [
      {
        field: event.active,
        order: event.direction === 'asc' ? OrderType.Ascending : OrderType.Descending,
      },
    ];

    this.loadCategoryGroups();
  }

  handlePagination({ pageIndex, pageSize }: TablePaginator) {
    this.searchOptions = {
      ...this.searchOptions,
      page: pageIndex + 1,
      pagesize: pageSize,
    };

    this.loadCategoryGroups();
  }

  handleAction({ actionId, data }: ActionEventType<DropdownActionsEnum, SOVCategoryGroupRow>) {
    switch (actionId) {
      case DropdownActionsEnum.EDIT:
        this.currentCategoryGroup.set(data);
        break;
      case DropdownActionsEnum.DELETE:
        this.isLoading.set(true);
        this.dialog
          .open(ConfirmationDialog, {
            data: {
              title: 'categoryGroup.deleteTitle',
              description: 'categoryGroup.deleteMessage',
            },
          })
          .afterClosed()
          .pipe(
            filter(val => !!val),
            finalize(() => this.isLoading.set(false)),
          )
          .subscribe(() => {
            this.isLoading.set(true);
            this.categoryGroupService
              .delete(data.id)
              .pipe(finalize(() => this.isLoading.set(false)))
              .subscribe(() => {
                this.notification.success('categoryGroup.deletedSuccessfully');
                this.loadCategoryGroups();
              });
          });
        break;
      case DropdownActionsEnum.ENABLE:
        this.isLoading.set(true);
        this.categoryGroupService
          .toggleDisable(data.id)
          .pipe(finalize(() => this.isLoading.set(false)))
          .subscribe(() => {
            this.notification.success('categoryGroup.enabledSuccessfully');
            this.loadCategoryGroups();
          });
        break;
      case DropdownActionsEnum.DISABLE:
        this.isLoading.set(true);
        this.categoryGroupService
          .toggleDisable(data.id)
          .pipe(finalize(() => this.isLoading.set(false)))
          .subscribe(() => {
            this.notification.success('categoryGroup.disabledSuccessfully');
            this.loadCategoryGroups();
          });
        break;
      case DropdownActionsEnum.DUPLICATE:
        this.currentCategoryGroup.set({
          id: 0,
          name: '',
          disabled: false,
          categories: [...(data.categories ?? [])].map((cat, index) => ({
            ...cat,
            id: 0,
            position: index,
            modified: true,
            uniqueKey: crypto.randomUUID(),
            visible: true,
            isUsed: false,
          })),
        });
        this.anythingChanged.set(true);
        break;
    }
  }

  addNewCategoryGroup() {
    this.currentCategoryGroup.set({
      id: 0,
      name: '',
      disabled: false,
      categories: [],
    });

    this.anythingChanged.set(true);
  }

  onCloseClick() {
    if (this.anythingChanged()) {
      this.dialog
        .open(ConfirmationDialog, {
          data: {
            title: 'categoryGroup.goBackErrorTitle',
            description: 'categoryGroup.goBackErrorMessage',
          },
        })
        .afterClosed()
        .pipe(filter(val => !!val))
        .subscribe(() => {
          this.onClose.emit(true);
        });
    } else {
      this.onClose.emit(true);
    }
  }

  returnFromCategoryGroupView() {
    if (this.anythingChanged()) {
      this.discardChanges();
    }

    this.currentCategoryGroup.set(null);
  }

  discardChanges() {
    this.anythingChanged.set(false);
    this.loadCategoryGroups();
    this.categoryGroupViewComponent().discardChanges();
  }

  saveCategoryGroup() {
    this.isLoading.set(true);
    const categoryGroup = this.currentCategoryGroup();
    // Remove the one that we want to delete
    categoryGroup.categories = categoryGroup.categories.filter(cat => !cat.markedForDeletion);

    // Check if we need to create or update
    if (categoryGroup.id === 0) {
      this.categoryGroupService
        .create(categoryGroup)
        .pipe(finalize(() => this.isLoading.set(false)))
        .subscribe(() => {
          this.notification.success('categoryGroup.createdSuccessfully');
          this.anythingChanged.set(false);
          this.currentCategoryGroup.set(null);
          this.loadCategoryGroups();
        });
    } else {
      this.categoryGroupService
        .update(categoryGroup)
        .pipe(finalize(() => this.isLoading.set(false)))
        .subscribe(() => {
          this.notification.success('categoryGroup.updatedSuccessfully');
          this.anythingChanged.set(false);
          this.currentCategoryGroup.set(null);
          this.loadCategoryGroups();
        });
    }
  }
}
